import { useEffect, useState } from "react";
import { Eye, EyeOff, ShieldAlert, Copy } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export function CardRevealModal({ booking, open, onOpenChange, onRevealed }) {
  const [reason, setReason] = useState("");
  const [card, setCard] = useState(null);
  const [left, setLeft] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (!open) { setCard(null); setReason(""); setLeft(0); } }, [open]);
  useEffect(() => {
    if (!card) return;
    if (left <= 0) { setCard(null); return; }
    const t = setTimeout(() => setLeft(left - 1), 1000);
    return () => clearTimeout(t);
  }, [card, left]);

  async function reveal() {
    setBusy(true);
    try {
      const r = await api.post(`/bookings/${booking.id}/card/reveal`, { reason });
      setCard(r.data); setLeft(45); onRevealed?.();
    } catch (e) { toast.error(apiError(e, "Reveal failed")); }
    finally { setBusy(false); }
  }
  const copy = (v, label) => { navigator.clipboard?.writeText(v); toast.success(`${label} copied`); };
  const pretty = (n) => n.replace(/(\d{4})(?=\d)/g, "$1 ");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-md" data-testid="card-reveal-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-amber-400" />Secure card reveal</DialogTitle>
          <DialogDescription className="text-slate-400">Booking <span className="font-mono text-slate-200">{booking.booking_ref}</span>. This action is written to the audit log with your identity and reason.</DialogDescription>
        </DialogHeader>
        {!card ? (
          <div className="space-y-3">
            <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">Only reveal when you are ready to key the charge into the terminal. Data auto-hides after 45 seconds.</div>
            <label className="block text-sm"><span className="text-slate-400">Reason for access</span>
              <input data-testid="reveal-reason-input" className="field mt-1" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Charging on AirTktSupport terminal" autoFocus /></label>
            <Button data-testid="reveal-confirm-btn" disabled={busy || reason.trim().length < 3} onClick={reveal} className="w-full bg-amber-600 hover:bg-amber-500 text-slate-950"><Eye className="h-4 w-4 mr-2" />Reveal card data</Button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="revealed-card">
            <div className="rounded-lg border border-slate-700 bg-slate-950 p-5 space-y-4">
              <div className="flex justify-between text-xs text-slate-500"><span>{card.card_type}</span><span data-testid="reveal-countdown">auto-hide in {left}s</span></div>
              <div className="flex items-center justify-between">
                <span data-testid="revealed-number" className="font-mono text-xl tracking-widest">{pretty(card.number)}</span>
                <button data-testid="copy-number-btn" onClick={() => copy(card.number, "Card number")} className="text-slate-400 hover:text-white"><Copy className="h-4 w-4" /></button>
              </div>
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><div className="eyebrow">Holder</div><div className="mt-1">{card.holder_name}</div></div>
                <div><div className="eyebrow">Expiry</div><div className="mt-1 font-mono" data-testid="revealed-expiry">{card.expiry}</div></div>
                <div><div className="eyebrow">Security</div><div className="mt-1 font-mono" data-testid="revealed-cvv">{card.cvv}</div></div>
              </div>
            </div>
            <Button data-testid="hide-card-btn" variant="outline" className="w-full border-slate-700" onClick={() => setCard(null)}><EyeOff className="h-4 w-4 mr-2" />Hide now</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
